import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import type { UserSession } from '@/api/types';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { StatTile } from '@/components/StatTile';
import { FlowsPanel, ShapeNotCollected } from '@/components/flows';
import { LiveDot, SyncNote } from '@/components/presence';
import { formatCompact } from '@/components/TrendChart';
import { formatDateTime } from '@/components/format';
import { formatWhen } from './AccountsPage';

/**
 * One person: when they signed in, for how long, from what, and how each session ended.
 *
 * This is the most disclosive page in the product — it says that a named individual was at a
 * keyboard at a particular time — so the route behind it is gated on `telemetry:agent_detail`
 * rather than plain read, and a viewer who follows a link here is told why rather than shown a
 * blank page.
 */
export function UserDetailPage() {
  const { tenantId = '', userId = '' } = useParams();

  const sessions = useQuery({
    queryKey: ['user-sessions', tenantId, userId],
    queryFn: () => api.presence.userSessions(tenantId, userId),
    retry: false,
  });

  // The features this person uses. Phase 1 built this endpoint and nothing has ever called it —
  // "what do they do" belongs beside "when are they here", and this is where both are asked.
  const features = useQuery({
    queryKey: ['agent-features', tenantId, userId],
    queryFn: () => api.telemetry.agentFeatures(tenantId, userId),
    retry: false,
  });

  // And the ORDER they do it in — which feature they open first, what they move to, how far they get.
  const flows = useQuery({
    queryKey: ['agent-paths', tenantId, userId],
    queryFn: () => api.paths.agent(tenantId, userId),
    retry: false,
  });

  const forbidden = sessions.error instanceof ApiError && sessions.error.status === 403;

  return (
    <>
      <div>
        <Link to={`/accounts/${tenantId}`} className="t-label" style={{ color: 'var(--primary)' }}>
          ← Back to account
        </Link>
        <h1 className="t-display" style={{ margin: '8px 0 4px' }}>Session history</h1>
      </div>

      {forbidden && (
        <Callout tone="warning" title="Your role cannot see per-person telemetry">
          Presence and seat counts are available to every role on the account page. A session
          timeline names an individual and says when they were signed in and from what, which needs
          <code> controlplane:telemetry:agent_detail</code> — analyst or owner.
        </Callout>
      )}

      {sessions.isError && !forbidden && (
        <Callout tone="error" title="Could not load sessions">
          {sessions.error instanceof ApiError ? sessions.error.message : 'Unexpected error.'}
        </Callout>
      )}

      {sessions.isLoading && <div className="empty-state"><span className="spinner" /></div>}

      {sessions.data && (
        <>
          {!sessions.data.stats_collected && (
            <Callout tone="warning" title="No session summary for this person">
              The totals below are a floor derived from the stored rows, not a count. The collector
              has not landed a stats row for this user — that is a gap in collection, not a person
              who has never signed in.
            </Callout>
          )}

          <div className="kpi-row">
            <StatTile
              label="Sessions"
              value={formatCompact(sessions.data.session_count)}
              sub={sessions.data.truncated
                ? `${sessions.data.sessions.length} most recent shown`
                : 'all shown'}
            />
            <StatTile
              label="Average length"
              /* Null when nothing has closed. A running session has no length, so an account whose
                 only session is open has no average — which is not zero minutes. */
              value={sessions.data.avg_session_minutes === null
                ? null
                : `${Math.round(sessions.data.avg_session_minutes)}m`}
              sub={`over ${formatCompact(sessions.data.closed_session_count)} closed`}
            />
            <StatTile
              label="Live now"
              value={sessions.data.live_session_count}
              sub={sessions.data.live_session_count > 0 ? 'signed in' : 'not signed in'}
            />
            <StatTile
              label="Ended by an admin"
              value={sessions.data.admin_terminated_count}
              tone={sessions.data.admin_terminated_count > 0 ? 'warning' : 'default'}
              sub="forced sign-outs"
            />
          </div>

          <div style={{ marginTop: -8 }}>
            <SyncNote syncedAt={sessions.data.synced_at} />
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>Sessions</h2>
              <span className="t-label subtle">newest first</span>
            </div>

            {sessions.data.sessions.length === 0
              ? <div className="empty-state t-body">No sessions recorded for this person.</div>
              : <SessionTimeline sessions={sessions.data.sessions} />}

            {/* The cap is announced. A shortened timeline that says nothing reads as the whole one,
                and "12 sessions" beside a list of 50 is how somebody concludes wrongly. */}
            {sessions.data.truncated && (
              <p className="t-caption subtle" style={{ margin: '12px 0 0' }}>
                Showing the {sessions.data.sessions.length} most recent of{' '}
                {formatCompact(sessions.data.session_count)}. Older sessions are counted in the
                totals above but not stored individually.
              </p>
            )}
          </div>

          {flows.data && (
            <>
              {!flows.data.shape_collected ? (
                <ShapeNotCollected />
              ) : (
                <div className="kpi-row">
                  <StatTile
                    label="Working sessions"
                    value={flows.data.sessions}
                    sub={`gaps over ${flows.data.flows.working_session_gap_minutes}m split them`}
                  />
                  <StatTile
                    label="Average depth"
                    /* Null, not 0 — an agent with no observed session has no average, and 0 would
                       rank them below people who worked and did little. */
                    value={flows.data.avg_steps === null
                      ? null
                      : `${Math.round(flows.data.avg_steps)} steps`}
                    sub="changes per session"
                  />
                  <StatTile
                    label="Usually starts at"
                    value={flows.data.entry_display_name ?? null}
                    sub={flows.data.exit_display_name ? `ends at ${flows.data.exit_display_name}` : undefined}
                  />
                  <StatTile
                    label="Cross-service work"
                    value={flows.data.cross_service_operations}
                    sub="operations spanning two or more apps"
                  />
                </div>
              )}

              <FlowsPanel flows={flows.data.flows} title="How they move" />
            </>
          )}

          {features.data && features.data.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="t-heading" style={{ margin: 0 }}>What they use</h2>
                <span className="t-label subtle">last 30 days</span>
              </div>
              <BarList
                rows={features.data.map((f) => ({ label: f.display_name, value: f.event_count }))}
                limit={8}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * The sessions as a timeline, following `LeadTimeline`'s structure.
 *
 * Absolute timestamps rather than "3 days ago": the gaps between sign-ins are the story here, and
 * relative labels flatten a fortnight of silence into the same shape as a busy week.
 */
function SessionTimeline({ sessions }: { sessions: UserSession[] }) {
  return (
    <ol className="lead-timeline">
      {sessions.map((s) => (
        <li className="timeline-item" key={s.login_session_id}>
          <span className={`timeline-dot ${dotTone(s)}`} aria-hidden="true" />
          <div className="timeline-body">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{formatDateTime(s.login_at)}</strong>
              {s.is_live && <LiveDot live label="live now" />}
              {s.ended_by_admin && (
                <span className="chip chip-down">
                  ended by an admin{s.terminated_reason ? ` · ${s.terminated_reason}` : ''}
                </span>
              )}
            </div>

            <div className="t-caption muted">
              {/* An open session shows no length rather than a length measured to now — the latter
                  changes every time the page is refreshed. */}
              {s.duration_minutes === null
                ? <span>still open</span>
                : <span>{Math.round(s.duration_minutes)} minutes</span>}
              {' · '}
              {[s.browser, s.os, s.device_type].filter(Boolean).join(' / ') || 'device not collected'}
              {' · '}
              {s.access_type === '-' ? 'access type not collected' : s.access_type}
              {/* Country is deliberately absent everywhere today — nothing derives it. Rendering a
                  blank column would invite somebody to read it as "unknown location". */}
              {s.country && ` · ${s.country}`}
            </div>

            {s.logout_at && (
              <div className="t-caption subtle">signed out {formatWhen(s.logout_at)}</div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Only the reserved status tones, and only where the status is a real finding. */
function dotTone(s: UserSession): string {
  if (s.is_live) return 'timeline-dot-good';
  if (s.ended_by_admin) return 'timeline-dot-bad';

  // An ordinary session that ended normally is not a finding, so it keeps the neutral default.
  return '';
}
